/**
 * AdminCompareSettings - Admin settings for Compare Properties feature
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Scale, Save, Loader2, Bell, Share2, FileText, Bookmark, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCompareSettings() {
  const [settings, setSettings] = useState({
    enabled: true,
    max_properties: 4,
    share_enabled: true,
    pdf_export_enabled: true,
    save_enabled: true,
    price_alerts_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    fetchSettings();
  }, []);
  
  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/compare/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load compare settings');
    } finally {
      setLoading(false);
    }
  };
  
  const saveSettings = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      Object.entries(settings).forEach(([key, value]) => {
        params.append(key, value);
      });
      
      const response = await fetch(`${API_URL}/api/compare/settings?${params.toString()}`, {
        method: 'PUT'
      });
      
      if (response.ok) {
        toast.success('Compare settings saved successfully');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card data-testid="admin-compare-settings">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Scale className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <CardTitle>Property Comparison</CardTitle>
              <CardDescription>Configure compare feature settings</CardDescription>
            </div>
          </div>
          <Badge variant={settings.enabled ? "default" : "secondary"}>
            {settings.enabled ? "Enabled" : "Disabled"}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Main toggle */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="text-base font-medium">Enable Compare Feature</Label>
            <p className="text-sm text-gray-500 mt-1">
              Turn on/off the entire property comparison feature
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={() => handleToggle('enabled')}
            data-testid="toggle-compare-enabled"
          />
        </div>
        
        {!settings.enabled && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">Compare feature is currently disabled for all users</span>
          </div>
        )}
        
        {/* Max properties */}
        <div className="space-y-2">
          <Label>Maximum Properties to Compare</Label>
          <div className="flex items-center gap-4">
            <Input
              type="number"
              min={2}
              max={10}
              value={settings.max_properties}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                max_properties: Math.min(10, Math.max(2, parseInt(e.target.value) || 4))
              }))}
              className="w-24"
              data-testid="max-properties-input"
            />
            <span className="text-sm text-gray-500">properties (min: 2, max: 10)</span>
          </div>
        </div>
        
        {/* Feature toggles */}
        <div className="grid gap-4">
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <Share2 className="h-4 w-4 text-blue-500" />
              <div>
                <Label className="text-sm font-medium">Share Comparison</Label>
                <p className="text-xs text-gray-500">Allow sharing via link</p>
              </div>
            </div>
            <Switch
              checked={settings.share_enabled}
              onCheckedChange={() => handleToggle('share_enabled')}
              disabled={!settings.enabled}
              data-testid="toggle-share"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-green-500" />
              <div>
                <Label className="text-sm font-medium">PDF Export</Label>
                <p className="text-xs text-gray-500">Allow PDF download</p>
              </div>
            </div>
            <Switch
              checked={settings.pdf_export_enabled}
              onCheckedChange={() => handleToggle('pdf_export_enabled')}
              disabled={!settings.enabled}
              data-testid="toggle-pdf"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <Bookmark className="h-4 w-4 text-purple-500" />
              <div>
                <Label className="text-sm font-medium">Save Comparisons</Label>
                <p className="text-xs text-gray-500">Allow saving to account</p>
              </div>
            </div>
            <Switch
              checked={settings.save_enabled}
              onCheckedChange={() => handleToggle('save_enabled')}
              disabled={!settings.enabled}
              data-testid="toggle-save"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="h-4 w-4 text-orange-500" />
              <div>
                <Label className="text-sm font-medium">Price Alerts</Label>
                <p className="text-xs text-gray-500">Allow price drop notifications</p>
              </div>
            </div>
            <Switch
              checked={settings.price_alerts_enabled}
              onCheckedChange={() => handleToggle('price_alerts_enabled')}
              disabled={!settings.enabled}
              data-testid="toggle-price-alerts"
            />
          </div>
        </div>
        
        {/* Save button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            onClick={saveSettings}
            disabled={saving}
            data-testid="save-compare-settings"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
